// "Play again" for a finished room, shared with the mobile app.
//
// The host's tap calls the createReplay Cloud Function, which in one
// transaction creates the new lobby and writes `rematch` on the finished room.
// The lobby id comes from the finished room, so a double tap or a retry returns
// the same game. Everyone still on the final screen already listens to the
// finished room, so they see the invite with no extra listener. Accepting is an
// ordinary join. Nothing counts against the hosting allowance until the new
// game starts.
//
// Room-document contract (additive fields only):
//   finished room: rematch { roomId, shortCode, hostId, hostName, createdAt, sequence }
//   new room:      rematchOf, rematchSequence, invitedPlayers [{ id, name }]
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import authService from "./authService";
import analytics from "./analyticsService";
import hostingService from "./hostingService";
import joinRoomService from "./joinRoomService";
import { statsService } from "./statisticsService";

class RematchService {
  async startRematch(oldRoom, roomDocId, hostName) {
    analytics.track("rematch_tapped", { room_id: roomDocId });
    try {
      const result = await hostingService.createReplay(roomDocId);
      if (!result.ok) {
        analytics.track("rematch_failed", { room_id: roomDocId, reason: result.reason });
        return { success: false, reason: result.reason };
      }
      this.openLobby({ roomId: result.roomId }, hostName, true);
      await authService.addRoomToUser(result.roomId, { roomName: result.roomName, isHost: true });
      if (!result.existing) {
        statsService.trackGameCreated({ ...oldRoom, roomId: result.roomId, isPublic: false }).catch(() => {});
      }
      analytics.track("rematch_started", {
        room_id: result.roomId,
        invited_count: Math.max(0, (oldRoom.players?.length || 1) - 1),
        existing: !!result.existing,
      });
      return { success: true, roomId: result.roomId, existing: !!result.existing };
    } catch (error) {
      console.error("Error starting rematch:", error);
      analytics.track("rematch_failed", { room_id: roomDocId, reason: error.code || "request_failed" });
      return { success: false, reason: "error" };
    }
  }

  // Point this device at the new lobby.
  openLobby(rematch, playerName, isHost) {
    localStorage.setItem("currentRoomId", rematch.roomId);
    localStorage.setItem("playerName", playerName);
    localStorage.setItem("isHost", isHost ? "true" : "false");
  }

  // A player accepts the invite: an ordinary join of the new room, after
  // checking the game has not started in the meantime.
  async acceptRematch(rematch, playerName) {
    const snap = await getDoc(doc(db, "rooms", rematch.roomId));
    if (!snap.exists() || snap.data().gameStatus !== "waiting" || snap.data().isActive === false) {
      analytics.track("rematch_invite_failed", { room_id: rematch.roomId, reason: "started" });
      return { success: false, reason: "started" };
    }
    const result = await joinRoomService.joinRoom(rematch.roomId, playerName, "rematch");
    if (result.success) {
      analytics.track("rematch_accepted", { room_id: rematch.roomId });
    } else {
      analytics.track("rematch_invite_failed", { room_id: rematch.roomId, reason: "error" });
    }
    return result;
  }

  declineRematch(rematch) {
    analytics.track("rematch_declined", { room_id: rematch.roomId });
  }
}

export const rematchService = new RematchService();
export default rematchService;
